import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../services/authService';
import { getUserReceipts, Receipt } from '../services/dbService';

// Helper function to format dates safely
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/A';
  
  try {
    // If it's a Firebase timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    
    // If it's a string or number, try to convert it
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString();
  } catch (err) {
    console.error('Error formatting date:', err);
    return 'N/A';
  }
};

// Safely extract amount from receipt
const safeGetAmount = (receipt: Receipt): number => {
  if (receipt.amount === undefined || receipt.amount === null) {
    return 0;
  }
  
  // Make sure it's a number
  const amount = typeof receipt.amount === 'number' 
    ? receipt.amount 
    : parseFloat(receipt.amount as unknown as string);
    
  // Return 0 if it's not a valid number
  return isNaN(amount) ? 0 : amount;
};

const Dashboard: React.FC = () => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchReceipts = async () => {
      try {
        setDebugInfo('Checking for current user...');
        const user = getCurrentUser();
        
        if (!user) {
          console.error('No authenticated user found in Dashboard');
          setDebugInfo('No authenticated user found');
          setError('You must be logged in to view this page. Please log in again.');
          setLoading(false);
          return;
        }
        
        setDebugInfo(`User found: ${user.uid}. Fetching receipts...`);
        console.log('Fetching receipts for user:', user.uid);
        
        const userReceipts = await getUserReceipts(user.uid);
        console.log('Receipts fetched:', userReceipts.length);
        setDebugInfo(`Fetched ${userReceipts.length} receipts`);
        
        // Ensure all receipts have basic required properties to avoid rendering issues
        const validatedReceipts = userReceipts.map(receipt => ({
          ...receipt,
          id: receipt.id || `temp-${Math.random().toString(36).substring(2, 9)}`,
          date: receipt.date || new Date(),
          bolNumber: receipt.bolNumber || 'Unknown',
          carrierName: receipt.carrierName || 'Unknown',
          amount: receipt.amount || 0,
          status: receipt.status || 'pending'
        }));
        
        if (isMounted) {
          setReceipts(validatedReceipts);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error in Dashboard fetchReceipts:', err);
        setDebugInfo(`Error: ${err.message || 'Unknown error'}`);
        
        if (isMounted) {
          setError(err.message || 'Failed to load dashboard data');
          setLoading(false);
          // Set empty receipts array to prevent rendering issues
          setReceipts([]);
        }
      }
    };
    
    fetchReceipts();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Calculate statistics with safer approaches
  const totalReceipts = receipts.length;
  const pendingReceipts = receipts.filter(r => r.status === 'pending').length;
  const paidReceipts = receipts.filter(r => r.status === 'paid').length;
  const totalAmount = receipts.reduce((sum, r) => sum + safeGetAmount(r), 0);
  
  // Get recent receipts (last 5) with safer sorting
  const recentReceipts = [...receipts]
    .sort((a, b) => {
      try {
        // Convert dates safely - check for Firebase timestamp first
        let dateA: Date;
        if (a.date && typeof a.date === 'object' && 'toDate' in a.date && typeof a.date.toDate === 'function') {
          dateA = a.date.toDate();
        } else if (a.date instanceof Date) {
          dateA = a.date;
        } else {
          dateA = new Date(a.date || 0);
        }
        
        let dateB: Date;
        if (b.date && typeof b.date === 'object' && 'toDate' in b.date && typeof b.date.toDate === 'function') {
          dateB = b.date.toDate();
        } else if (b.date instanceof Date) {
          dateB = b.date;
        } else {
          dateB = new Date(b.date || 0);
        }
        
        // Handle invalid dates by ensuring they don't cause sorting errors
        const timeA = !dateA || isNaN(dateA.getTime()) ? 0 : dateA.getTime();
        const timeB = !dateB || isNaN(dateB.getTime()) ? 0 : dateB.getTime();
        
        return timeB - timeA;
      } catch (error) {
        console.error('Error sorting dates:', error);
        return 0;
      }
    })
    .slice(0, 5);
  
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading">Loading dashboard data...</div>
        <div className="debug-info">Debug: {debugInfo}</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-message">{error}</div>
        <div className="debug-info">Debug: {debugInfo}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="dashboard">
      <h1>NFI Lumper Receipt Creator | Dashboard</h1>
      
      <div className="stats-container">
        <div className="stat-card">
          <h3>Total Receipts</h3>
          <div className="stat-value">{totalReceipts}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Receipts</h3>
          <div className="stat-value">{pendingReceipts}</div>
        </div>
        <div className="stat-card">
          <h3>Paid Receipts</h3>
          <div className="stat-value">{paidReceipts}</div>
        </div>
        <div className="stat-card">
          <h3>Total Amount</h3>
          <div className="stat-value">${totalAmount.toFixed(2)}</div>
        </div>
      </div>
      
      <div className="recent-receipts">
        <div className="section-header">
          <h2>Recent Receipts</h2>
          <Link to="/receipts" className="view-all-link">View All</Link>
        </div>
        
        {recentReceipts.length === 0 ? (
          <p className="no-data">No receipts found. Create your first receipt using the quick actions below.</p>
        ) : (
          <table className="receipts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>BOL Number</th>
                <th>Carrier</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentReceipts.map(receipt => (
                <tr key={receipt.id}>
                  <td>{formatDate(receipt.date)}</td>
                  <td>{receipt.bolNumber || 'N/A'}</td>
                  <td>{receipt.carrierName || 'N/A'}</td>
                  <td>${(safeGetAmount(receipt)).toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${receipt.status || 'pending'}`}>
                      {receipt.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/manual-receipt" className="action-button">
            Create Manual Receipt
          </Link>
          <Link to="/batch-receipt" className="action-button">
            Batch Receipt Processing
          </Link>
          <Link to="/settings" className="action-button">
            Scan Emails
          </Link>
          <Link to="/carriers" className="action-button">
            Manage Carriers
          </Link>
          <Link to="/lanes" className="action-button">
            Manage Lanes
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 